'use client';

import { useState, useCallback, useEffect, useRef } from 'react';
import type { InteractionStorageAdapter } from '../../model/interaction/adapters';
import { INTERACTION_STORAGE_KEYS } from '../../constants/interaction/constants';

export interface UseOnboardingModalOptions {
  enabled: boolean;
  storage: InteractionStorageAdapter;
}

interface UseOnboardingModalReturn {
  /** True when the 20% auto-show roll succeeded and hasn't been shown yet */
  shouldAutoShow: boolean;
  /** Mark the auto-show as complete (persists to storage) */
  markAutoShowComplete: () => void;
}

/**
 * Controls the one-time auto-show onboarding modal with a 20% probability.
 * Manual open (click) is handled separately in the orchestrator — always allowed.
 *
 * The random roll + storage write happen inside useEffect (not useState)
 * so React Strict Mode's double-invocation of initializers cannot
 * persist the flag before the kept render reads it.
 */
export function useOnboardingModal({
  enabled,
  storage,
}: UseOnboardingModalOptions): UseOnboardingModalReturn {
  const [shouldAutoShow, setShouldAutoShow] = useState(false);
  const hasRolledRef = useRef(false);

  useEffect(() => {
    if (!enabled || hasRolledRef.current) {
      return;
    }
    hasRolledRef.current = true;

    const alreadyAutoShown = storage.getItem<boolean>(
      INTERACTION_STORAGE_KEYS.onboardingAutoShown,
      false,
    );

    if (alreadyAutoShown) {
      return;
    }

    if (Math.random() < 0.2) {
      storage.setItem(INTERACTION_STORAGE_KEYS.onboardingAutoShown, true);
      setShouldAutoShow(true);
    }
  }, [enabled, storage]);

  const markAutoShowComplete = useCallback(() => {
    storage.setItem(INTERACTION_STORAGE_KEYS.onboardingAutoShown, true);
  }, [storage]);

  return {
    shouldAutoShow,
    markAutoShowComplete,
  };
}
